import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { captureSetLanguage, ViewsByTypes } from "src/api/analytic";
import urls from "src/helpers/urls";
import { v4 as uuidv4 } from "uuid";
import { getApp } from "../api-client";
import Socket from "../helpers/Socket";
import {
  Area,
  Content,
  ContentLanguage,
  Exhibit,
  Location,
  Organization,
} from "../types";

interface AppContextType {
  org: Organization | null;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  selectedAreaId: string | null;
  selectedExhibitId: string | null;
  setSelectedAreaId: (areaId: string | null) => void;
  setSelectedExhibitId: (exhibitId: string | null) => void;
  getAreaName: (areaId: string) => string;
  getExhibitName: (exhibitId: string) => string;
  getVisibleExhibitsByAreaId: (areaId: string) => Exhibit[];
  getContentsByExhibitId: (exhibitId: string) => Content[];
  getLocation: () => Location | null;
  getOrganization: () => Organization | null;
  getArea: (areaId: string) => Area | null;
  getExhibit: (exhibitId: string) => Exhibit | null;
  getUdid: () => string | null;
  getContentForLanguage: (content: Content) => ContentLanguage | null;
  getSelectedExhibit: () => Exhibit | null;
  getSelectedArea: () => Area | null;
  getSelectedContents: (id?: string) => Content[] | null;
  setVisitedAreas: (areaId: string) => void;
  setVisitedExhibits: (exhibitId: string) => void;
  visitedExhibits: () => string[];
  visitedAreas: () => string[];
  getVisibleAreas: () => Area[];
  getVisibleExhibits: (exhibitId: string) => Exhibit[];
  setHasCoordinates: (hasCoordinates: boolean) => void;
  hasCoordinates: boolean;
  showFAB: boolean;
  setShowFAB: (showFAB: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);
export const AppProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [org, setOrg] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedExhibitId, setSelectedExhibitId] = useState<string | null>(
    null
  );
  const [hasCoordinates, setHasCoordinates] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const [showFAB, setShowFAB] = React.useState(false);
  // Initialize selectedLanguage from localStorage or navigator.language
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");

  // Initialize socket connection
  // this only runs once when the component mounts
  React.useEffect(() => {
    // Set the udid in the socket
    Socket.setAppContext({ getUdid: () => getUdid() });
    // Initialize the socket connection ONCE
    Socket.init(() => {
      setIsSocketConnected(true);
    });

    // Cleanup function to disconnect socket when component unmounts
    return () => {
      if (Socket.socket && Socket.socket.disconnect) {
        Socket.socket.disconnect();
      }
    };
  }, []);

  // Get exhibitId from URL params

  React.useEffect(() => {
    const topic = window.location.pathname.split("/")[1];
    const topicId = window.location.pathname.split("/")[2];
    if (topic === "topic" && topicId) {
      if (selectedExhibitId !== topicId) {
        setSelectedExhibitId(topicId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  // useEffect to load the app data, triggered when the org or selectedExhibitId changes
  React.useEffect(() => {
    if (!selectedExhibitId) return;
    // Only push state if the URL is different
    const viewsBy = new URLSearchParams(window.location.search).get("feature");
    const feature = !selectedAreaId ? viewsBy : ViewsByTypes.menu;
    const newUrl = urls.exhibit(selectedExhibitId, feature);
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.pushState({}, "", newUrl);
    }
    let foundExhibit = false;
    let areas = org?.location?.areas;
    let areasArray = areas ? Object.values(areas) : [];
    for (const area of areasArray) {
      const exhibit = area.exhibits?.[selectedExhibitId];
      if (exhibit) {
        foundExhibit = true;
        setShowFAB(exhibit?.enableSharing);
        setSelectedAreaId(area.id);
        updateLocalStorage("visitedAreas", area.id, area.status);
        updateLocalStorage(
          "visitedExhibits",
          selectedExhibitId,
          exhibit.status
        );
      }
    }
    // Check if the selected exhibit is found in the areaOrder, else fetch the app data
    if (!foundExhibit) {
      setIsLoading(true);
      (async () => {
        const data = await getApp(selectedExhibitId);
        if (data) {
          setOrg(data);
        } else {
          alert("Error loading app data");
        }
        setIsLoading(false);
      })();
    }
  }, [org?.location?.areas, selectedAreaId, selectedExhibitId]);

  const updateLocalStorage = (key: string, id: string, status: number) => {
    if (status === 1 || status === 3) {
      const items = JSON.parse(localStorage.getItem(key) || "[]");
      if (!items.includes(id)) {
        items.push(id);
        localStorage.setItem(key, JSON.stringify(items));
      }
    }
  };

  const getAreaName = (areaId: string): string => {
    if (!org) return "";

    const area = org.location.areas[areaId];
    if (area) {
      let activeLanguage = selectedLanguage;
      // Find the name in the active language
      const langName = area.name.find((n) => n.language === activeLanguage);
      if (langName) {
        return langName.name;
      }
      // Fallback to English if the active language is not found
      const enName = area.name.find((n) => n.language === "en");
      return enName ? enName.name : "Missing translation";
    }
    return "Missing name value or translation of value";
  };

  const getExhibitName = (exhibitId: string): string => {
    if (!org) return "";
    let exhibit = getExhibit(exhibitId);
    if (exhibit) {
      let activeLanguage = selectedLanguage;
      // Find the name in the active language
      const langName = exhibit.name.find((n) => n.language === activeLanguage);
      if (langName) {
        return langName.name;
      }
      // Fallback to default language if the active language is not found
      const defaultName = exhibit.name.find(
        (n) => n.language === getLocation().defaultLanguage
      );
      return defaultName ? defaultName.name : "Missing name or translation";
    }

    return "Missing name value or translation of value";
  };

  // Memoized getExhibit must come before any function that uses it
  const getExhibit = useCallback(
    (exhibitId: string): Exhibit | null => {
      if (!org) return null;
      for (const area of Object.values(org.location.areas)) {
        const exhibit = area.exhibits?.[exhibitId];
        if (exhibit) {
          return exhibit;
        }
      }
      return null;
    },
    [org]
  );

  const getContentsByExhibitId = useCallback(
    (exhibitId: string): Content[] => {
      if (!org) return [];
      let exhibit = getExhibit(exhibitId);
      if (exhibit && exhibit.contentOrder) {
        return exhibit.contentOrder.map(
          (contentId) => exhibit.content[contentId]
        );
      }
      return [];
    },
    [org, getExhibit]
  );

  const getLocation = useCallback((): Location | null => {
    if (!org) return null;
    return org.location;
  }, [org]);

  const getOrganization = (): Organization => {
    if (!org) return null;
    return org;
  };

  const getArea = useCallback(
    (areaId: string): Area | null => {
      if (!org) return null;
      return org.location.areas[areaId] || null;
    },
    [org]
  );

  const getSelectedArea = useCallback(() => {
    if (!org) return null;
    const area = org.location.areas[selectedAreaId];
    if (area) {
      return area;
    }
    return null;
  }, [org, selectedAreaId]);

  const getSelectedContents = useCallback(
    (id) => {
      if (!org) return [];
      let exhibit = getExhibit(id || selectedExhibitId);
      if (exhibit && exhibit?.contentOrder) {
        return exhibit.contentOrder
          .map((contentId) => exhibit?.content[contentId])
          .filter((content) => content); //sorted and filtered
      }
      return [];
    },
    [org, getExhibit, selectedExhibitId]
  );

  const getUdid = () => {
    let udid = window.localStorage.getItem("udid");
    if (!udid) {
      udid = uuidv4();
      window.localStorage.setItem("udid", udid);
    }
    return udid;
  };

  const getContentForLanguage = useCallback(
    (content: Content): ContentLanguage | null => {
      if (!content) return null;
      const langContent = content.languages.find(
        (c) => c.language === selectedLanguage
      );
      if (langContent) {
        return langContent;
      }
      // Fallback to default if the active language is not found
      const defaultContentLang = content.languages.find(
        (c) => c.language === getLocation().defaultLanguage
      );
      return defaultContentLang || null;
    },
    [selectedLanguage, getLocation]
  );

  const getSelectedExhibit = useCallback(() => {
    if (!org || !selectedExhibitId) return null;
    for (const area of Object.values(org.location.areas)) {
      const exhibit = area.exhibits?.[selectedExhibitId];
      if (exhibit) {
        return exhibit;
      }
    }
    return null;
  }, [org, selectedExhibitId]);

  // method to set visitedAreas
  const setVisitedAreas = (areaId: string) => {
    let visitedAreas = JSON.parse(localStorage.getItem("visitedAreas") || "[]");
    if (!visitedAreas.includes(areaId)) {
      visitedAreas.push(areaId);
      localStorage.setItem("visitedAreas", JSON.stringify(visitedAreas));
    }
  };

  // method to set visitedExhibits
  const setVisitedExhibits = (exhibitId: string) => {
    let visitedExhibits = JSON.parse(
      localStorage.getItem("visitedExhibits") || "[]"
    );
    const exhibit = getExhibit(exhibitId);
    if (!visitedExhibits.includes(exhibitId) && exhibit.status !== 4) {
      visitedExhibits.push(exhibitId);
      localStorage.setItem("visitedExhibits", JSON.stringify(visitedExhibits));
    }
  };

  // Memoized helpers for localStorage
  const visitedExhibits = useCallback(() => {
    return JSON.parse(localStorage.getItem("visitedExhibits") || "[]");
  }, []);

  const visitedAreas = useCallback(() => {
    return JSON.parse(localStorage.getItem("visitedAreas") || "[]");
  }, []);

  const getVisibleAreas = useCallback(() => {
    if (!org) return [];
    let areas = org.location.areaOrder
      .map((areaId) => org.location.areas?.[areaId])
      .filter((area) => area);
    const visibleAreas = areas.filter((area) => {
      const hasExhibits = area?.exhibitOrder?.length > 0;
      const isSelectedExhibitInArea = selectedExhibitId
        ? area?.exhibitOrder?.includes(selectedExhibitId)
        : false;
      const isVisitedArea = visitedAreas().includes(area.id);
      const isNotRestricted = area.status !== 3;
      return (
        hasExhibits &&
        (isSelectedExhibitInArea || isVisitedArea || isNotRestricted)
      );
    });
    return visibleAreas;
  }, [org, selectedExhibitId, visitedAreas]);

  const getVisibleExhibitsByAreaId = useCallback(
    (areaId: string): Exhibit[] => {
      if (!org) return [];
      let area = getArea(areaId);
      let exhibits = area?.exhibitOrder
        .map((exhibitId) => area?.exhibits?.[exhibitId])
        .filter((exhibit) => exhibit);
      return exhibits?.filter((exhibit) => {
        if (exhibit.id === selectedExhibitId) {
          return true;
        }
        if (exhibit.status === 3) {
          return visitedExhibits().includes(exhibit.id);
        }
        if (exhibit.status === 4 || exhibit.status === 2) {
          return false;
        }
        return true;
      });
    },
    [org, getArea, selectedExhibitId, visitedExhibits]
  );

  const getVisibleExhibits = useCallback(
    (exhibitId) => {
      if (!org) return [];
      let areaId = getExhibit(exhibitId)?.areaId;
      return getVisibleExhibitsByAreaId(areaId);
    },
    [org, getExhibit, getVisibleExhibitsByAreaId]
  );

  // ANALYTICS THINGS//

  // send analytics event when selectedExhibitId changes
  React.useEffect(() => {
    if (selectedExhibitId && isSocketConnected && org) {
      const location = getLocation();
      const organizationId = org?._id?.$oid;

      // alert("Exhibit ID: " + selectedExhibitId);
      // Emit the socket event
      Socket.onViewContent({
        areaId: getExhibit(selectedExhibitId)?.areaId,
        exhibitId: selectedExhibitId,
        locationId: location?._id?.$oid,
        organizationId,
      });
    }
  }, [selectedExhibitId, isSocketConnected, getLocation, getExhibit, org]);

  // useEffect to set Active language and send analytics event for it, only when org is loaded
  React.useEffect(() => {
    const storedLanguage = localStorage.getItem("activeLanguage");
    const deviceLanguage = navigator.language.split("-")[0];
    if (!storedLanguage) {
      captureSetLanguage(
        "SET_LANGUAGE_FROM_DEVICE",
        deviceLanguage,
        deviceLanguage,
        org
      );
      localStorage.setItem("activeLanguage", deviceLanguage);
      setSelectedLanguage(deviceLanguage);
    } else {
      if (selectedLanguage !== storedLanguage) {
        localStorage.setItem("activeLanguage", selectedLanguage);
        captureSetLanguage(
          "SET_ACTIVE_LANGUAGE",
          selectedLanguage,
          navigator.language.split("-")[0],
          org
        );
      }
    }
  }, [org, selectedLanguage]);

  return (
    <AppContext.Provider
      value={{
        org,
        selectedLanguage,
        setSelectedLanguage,
        isLoading,
        setIsLoading,
        selectedAreaId,
        selectedExhibitId,
        setSelectedAreaId,
        setSelectedExhibitId,
        getAreaName,
        getExhibitName,
        getVisibleExhibitsByAreaId,
        getContentsByExhibitId,
        getLocation,
        getArea,
        getExhibit,
        getOrganization,
        getUdid,
        getContentForLanguage,
        getSelectedExhibit,
        getSelectedArea,
        getSelectedContents,
        setVisitedAreas,
        setVisitedExhibits,
        visitedExhibits,
        visitedAreas,
        getVisibleAreas,
        getVisibleExhibits,
        setHasCoordinates,
        hasCoordinates,
        showFAB,
        setShowFAB,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
